/**
 * Propose Service Dialog
 *
 * Provider-facing submission form for ticket 86eya6nqh. Creates a `pending` proposal and
 * notifies Operations; Operations approves (auto-activating the service and adding it to
 * the provider's capable_services) or rejects with a reason.
 *
 * Writes: POST /api/providers/service-proposals/
 *
 * Controlled dumb component — the page owns the mutation.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { AlertCircle, Info, Loader2, Send } from 'lucide-react'
import { useServiceCategories } from '@/hooks/useServiceCategories'
import { useLocalization } from '@/contexts/LocalizationContext'
import { minutesToDurationString } from '@/types/providerServices'
import type { SubmitServiceProposalInput } from '@/types/providerServices'

/** Backend max_digits=10, decimal_places=2. Keep the client below that so it 400s less. */
const MAX_PRICE = 99_999_999
/** Cap at 24h so minutesToDurationString never needs DRF's "D HH:MM:SS" day form. */
const MAX_DURATION_MINUTES = 1440
const MIN_DURATION_MINUTES = 5

interface ProposeServiceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (input: SubmitServiceProposalInput) => Promise<void>
  isLoading?: boolean
  /** Message from a failed submit, rendered inline rather than as a toast. */
  submitError?: string | null
}

interface FormState {
  serviceName: string
  description: string
  proposedPrice: string
  durationMinutes: string
  categoryId: string
}

const EMPTY_FORM: FormState = {
  serviceName: '',
  description: '',
  proposedPrice: '',
  durationMinutes: '',
  categoryId: '',
}

export function ProposeServiceDialog({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
  submitError = null,
}: ProposeServiceDialogProps) {
  const { t, locale } = useLocalization()
  const { categories } = useServiceCategories(open)

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM)
      setTouched(false)
    }
  }, [open])

  const handleChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const errors = useMemo(() => {
    const next: Partial<Record<keyof FormState, string>> = {}

    if (!form.serviceName.trim()) {
      next.serviceName = t('myServices.validation.nameRequired')
    } else if (form.serviceName.trim().length > 255) {
      next.serviceName = t('myServices.validation.nameTooLong')
    }

    const price = Number(form.proposedPrice)
    if (!form.proposedPrice.trim()) {
      next.proposedPrice = t('myServices.validation.priceRequired')
    } else if (!Number.isFinite(price) || price <= 0 || price > MAX_PRICE) {
      next.proposedPrice = t('myServices.validation.priceInvalid')
    }

    const minutes = Number(form.durationMinutes)
    if (!form.durationMinutes.trim()) {
      next.durationMinutes = t('myServices.validation.durationRequired')
    } else if (
      !Number.isInteger(minutes) ||
      minutes < MIN_DURATION_MINUTES ||
      minutes > MAX_DURATION_MINUTES
    ) {
      next.durationMinutes = t('myServices.validation.durationRange')
    }

    return next
  }, [form, t])

  const isValid = Object.keys(errors).length === 0

  const handleOpenChange = (next: boolean) => {
    if (isLoading) return
    onOpenChange(next)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTouched(true)
    if (!isValid) return

    const input: SubmitServiceProposalInput = {
      service_name: form.serviceName.trim(),
      proposed_price: Number(form.proposedPrice).toFixed(2),
      estimated_duration: minutesToDurationString(Number(form.durationMinutes)),
    }
    const description = form.description.trim()
    if (description) input.description = description
    if (form.categoryId) input.category_id = form.categoryId

    try {
      await onSubmit(input)
      onOpenChange(false)
    } catch {
      // Parent surfaces the reason via submitError; keep the form open with its values.
    }
  }

  const showError = (field: keyof FormState) => touched && errors[field]

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
          <DialogTitle className="pe-10">{t('myServices.proposeService')}</DialogTitle>
          <DialogDescription>{t('myServices.proposeDescription')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-4 overflow-y-auto px-6 pb-4">
            {submitError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="whitespace-pre-line">{submitError}</AlertDescription>
              </Alert>
            )}

            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>{t('myServices.reviewNotice')}</AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="proposal-name">{t('myServices.serviceName')}</Label>
              <Input
                id="proposal-name"
                value={form.serviceName}
                onChange={(e) => handleChange('serviceName', e.target.value)}
                placeholder={t('myServices.serviceNamePlaceholder')}
                maxLength={255}
                disabled={isLoading}
              />
              {showError('serviceName') && (
                <p className="text-xs text-red-600">{errors.serviceName}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="proposal-description">{t('myServices.descriptionLabel')}</Label>
              <Textarea
                id="proposal-description"
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder={t('myServices.descriptionPlaceholder')}
                rows={3}
                maxLength={1000}
                disabled={isLoading}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="proposal-price">{t('myServices.proposedPrice')}</Label>
                <Input
                  id="proposal-price"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={form.proposedPrice}
                  onChange={(e) => handleChange('proposedPrice', e.target.value)}
                  disabled={isLoading}
                  dir="ltr"
                />
                {showError('proposedPrice') && (
                  <p className="text-xs text-red-600">{errors.proposedPrice}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="proposal-duration">
                  {t('myServices.estimatedDuration')}
                </Label>
                <Input
                  id="proposal-duration"
                  type="number"
                  inputMode="numeric"
                  step="5"
                  min={MIN_DURATION_MINUTES}
                  max={MAX_DURATION_MINUTES}
                  value={form.durationMinutes}
                  onChange={(e) => handleChange('durationMinutes', e.target.value)}
                  disabled={isLoading}
                  dir="ltr"
                />
                {showError('durationMinutes') && (
                  <p className="text-xs text-red-600">{errors.durationMinutes}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="proposal-category">{t('myServices.category')}</Label>
              <select
                id="proposal-category"
                value={form.categoryId}
                onChange={(e) => handleChange('categoryId', e.target.value)}
                disabled={isLoading}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">{t('myServices.categoryOptional')}</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {(locale === 'ar' ? c.nameAr : c.nameEn) || c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <DialogFooter className="flex-row justify-end gap-3 border-t px-6 py-4 shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isLoading} className="min-w-[140px]">
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin me-2" />
                  {t('common.submitting')}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 me-2" />
                  {t('myServices.submitProposal')}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
